import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Fab,
  Chip,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PowerSettingsNew as PowerIcon,
  SyncAlt as SyncAltIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
  Euro as EuroIcon,
  DateRange as DateIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import ObjektDokumenteDialog from './objekte/ObjektDokumenteDialog';
import { styled } from '@mui/material/styles';
import { FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch } from '@mui/material';

const EIGENTUEMER_TYP_LABELS = {
  PRIVATPERSON: 'Privatperson',
  GBR: 'GbR',
  KAPITALGESELLSCHAFT: 'Kapitalgesellschaft',
};

const RECHTSFORM_LABELS = {
  GMBH: 'GmbH',
  UG: 'UG (haftungsbeschränkt)',
  AG: 'AG',
  SONSTIGE: 'Sonstige',
};

const ownerTypLabel = (e) => (
  e.eigentuemer_typ === 'KAPITALGESELLSCHAFT'
    ? (RECHTSFORM_LABELS[e.rechtsform] || 'Kapitalgesellschaft')
    : (EIGENTUEMER_TYP_LABELS[e.eigentuemer_typ] || e.eigentuemer_typ)
);

// Styled Components für Triton Design
const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
  '& .MuiTableHead-root': {
    backgroundColor: '#f7fafc',
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    color: '#1a365d',
    borderBottom: '2px solid #e2e8f0',
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  margin: theme.spacing(0, 0.5),
  '&.edit': {
    color: '#1a365d',
    '&:hover': {
      backgroundColor: 'rgba(26, 54, 93, 0.1)',
    },
  },
  '&.delete': {
    color: '#e53e3e',
    '&:hover': {
      backgroundColor: 'rgba(229, 62, 62, 0.1)',
    },
  },
}));

const StyledFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)',
  color: '#ffffff',
  '&:hover': {
    background: 'linear-gradient(135deg, #2d3748 0%, #1a365d 100%)',
    transform: 'scale(1.05)',
  },
}));

const ObjectManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [objects, setObjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showInactive, setShowInactive] = useState(false);
  const [dokumenteObjekt, setDokumenteObjekt] = useState(null);
  const [eigentuemerListe, setEigentuemerListe] = useState([]);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [ownerForm, setOwnerForm] = useState({ bezeichnung: '', eigentuemer_typ: 'PRIVATPERSON', rechtsform: 'GMBH' });

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Reassign Dialog State
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignSourceId, setReassignSourceId] = useState(null);
  const [reassignTargetObjektId, setReassignTargetObjektId] = useState('');
  const [reassignTargetWohnungsId, setReassignTargetWohnungsId] = useState('');
  const [reassignUnits, setReassignUnits] = useState([]);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignAffected, setReassignAffected] = useState(null);
  
  // Formular-Daten
  const [formData, setFormData] = useState({
    objektkuerzel: '',
    objektstrasse: '',
    objekthausnummer: '',
    objektplz: '',
    objektort: '',
    objektkaufdatum: '',
    objektkaufpreis: '',
    objektgebaeudeanteilprozent: '',
    marktwert_faktor: '20.0',
    eigentuemer_id: '',
    anteil_prozent: '100',
  });

  // URL-Parameter prüfen (für "Neues Objekt" Link)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('action') === 'new') {
      handleOpenDialog();
      // URL bereinigen
      navigate('/dashboard/objekte', { replace: true });
    }
  }, [location.search, navigate]);

  const fetchObjects = useCallback(async () => {
    try {
      setLoading(true);
      const url = `/api/objects${showInactive ? '?includeInactive=1' : ''}`;
      const data = await apiService.get(url);
      setObjects(data);
    } catch (err) {
      console.error('Fehler beim Laden der Objekte:', err);
      showSnackbar('Fehler beim Laden der Objekte', 'error');
    } finally {
      setLoading(false);
    }
  }, [showInactive, showSnackbar]);

  // Objekte laden
  useEffect(() => {
    fetchObjects();
  }, [fetchObjects]);

  const fetchEigentuemer = useCallback(async () => {
    try {
      const data = await apiService.get('/api/eigentuemer');
      setEigentuemerListe(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Eigentümer:', err);
    }
  }, []);

  useEffect(() => {
    fetchEigentuemer();
  }, [fetchEigentuemer]);

  const handleToggleInactive = (e) => {
    setShowInactive(e.target.checked);
  };

  // Datum formatieren (dd.mm.yyyy)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Falls das Datum ungültig ist
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Preis formatieren
  const formatPrice = (price) => {
    if (!price && price !== 0) return '-';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  // Dialog öffnen (Neu oder Bearbeiten)
  const handleOpenDialog = (object = null) => {
    if (object) {
      setEditMode(true);
      setSelectedObject(object);
      setFormData({
        objektkuerzel: object.objektkuerzel || '',
        objektstrasse: object.objektstrasse || '',
        objekthausnummer: object.objekthausnummer || '',
        objektplz: object.objektplz || '',
        objektort: object.objektort || '',
        objektkaufdatum: object.objektkaufdatum ? object.objektkaufdatum.split('T')[0] : '',
        objektkaufpreis: object.objektkaufpreis || '',
        objektgebaeudeanteilprozent: object.objektgebaeudeanteilprozent || '',
        marktwert_faktor: object.marktwert_faktor || '20.0',
        eigentuemer_id: object.eigentuemer?.[0]?.eigentuemer_id || '',
        anteil_prozent: object.eigentuemer?.[0]?.anteil_prozent != null ? String(object.eigentuemer[0].anteil_prozent) : '100',
      });
    } else {
      setEditMode(false);
      setSelectedObject(null);
      setFormData({
        objektkuerzel: '',
        objektstrasse: '',
        objekthausnummer: '',
        objektplz: '',
        objektort: '',
        objektkaufdatum: '',
        objektkaufpreis: '',
        objektgebaeudeanteilprozent: '',
        marktwert_faktor: '20.0',
        eigentuemer_id: '',
        anteil_prozent: '100',
      });
    }
    setOpen(true);
  };

  // Dialog schließen
  const handleCloseDialog = () => {
    setOpen(false);
    setEditMode(false);
    setSelectedObject(null);
    setFormData({
      objektkuerzel: '',
      objektstrasse: '',
      objekthausnummer: '',
      objektplz: '',
      objektort: '',
      objektkaufdatum: '',
      objektkaufpreis: '',
      objektgebaeudeanteilprozent: '',
      marktwert_faktor: '20.0',
      eigentuemer_id: '',
      anteil_prozent: '100',
    });
  };

  // Formular-Handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Objekt speichern
  const handleSaveObject = async () => {
    try {
      setLoading(true);

      let objektId;
      if (editMode && selectedObject) {
        // Objekt aktualisieren
        await apiService.put(`/api/objects/${selectedObject.objektid}`, formData);
        objektId = selectedObject.objektid;
        showSnackbar('Objekt erfolgreich aktualisiert', 'success');
      } else {
        // Neues Objekt erstellen
        const created = await apiService.post('/api/objects', formData);
        objektId = created?.objektid;
        showSnackbar('Objekt erfolgreich erstellt', 'success');
      }

      if (objektId) {
        const zuordnungen = formData.eigentuemer_id
          ? [{ eigentuemer_id: formData.eigentuemer_id, anteil_prozent: parseFloat(formData.anteil_prozent) || 100 }]
          : [];
        try {
          await apiService.put(`/api/eigentuemer/objekt/${objektId}`, { zuordnungen });
        } catch (assignErr) {
          console.error('Fehler beim Zuordnen des Eigentümers:', assignErr);
          showSnackbar(assignErr?.response?.data?.error || 'Objekt gespeichert, aber Eigentümer-Zuordnung fehlgeschlagen', 'warning');
        }
      }

      handleCloseDialog();
      fetchObjects(); // Liste neu laden
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      showSnackbar('Fehler beim Speichern des Objekts', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Neuen Eigentümer inline anlegen
  const handleCreateOwner = async () => {
    try {
      const payload = {
        bezeichnung: ownerForm.bezeichnung,
        eigentuemer_typ: ownerForm.eigentuemer_typ,
      };
      if (ownerForm.eigentuemer_typ === 'KAPITALGESELLSCHAFT') {
        payload.rechtsform = ownerForm.rechtsform;
      }
      const created = await apiService.post('/api/eigentuemer', payload);
      await fetchEigentuemer();
      setFormData(prev => ({ ...prev, eigentuemer_id: created.eigentuemer_id }));
      setOwnerDialogOpen(false);
      setOwnerForm({ bezeichnung: '', eigentuemer_typ: 'PRIVATPERSON', rechtsform: 'GMBH' });
      showSnackbar('Eigentümer angelegt', 'success');
    } catch (err) {
      console.error('Fehler beim Anlegen des Eigentümers:', err);
      showSnackbar(err?.response?.data?.error || 'Fehler beim Anlegen des Eigentümers', 'error');
    }
  };

  // Objekt löschen
  const handleDeleteObject = async (objektId) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses Objekt löschen möchten?')) {
      try {
        await apiService.delete(`/api/objects/${objektId}`);
        showSnackbar('Objekt erfolgreich gelöscht', 'success');
        fetchObjects();
      } catch (err) {
        console.error('Fehler beim Löschen:', err);
        if (err?.response?.status === 409) {
          const data = err.response.data || {};
          const details = data.details || {};
          const pg = data.pg || {};
          const detailList = Object.entries(details).map(([k,v]) => `${k}: ${v}`).join(', ');
          const msg = data.error || pg.detail || 'Löschen nicht möglich (verknüpfte Daten vorhanden).';
          showSnackbar(`${msg}${detailList ? ' → ' + detailList : ''}`, 'error');
        } else {
          showSnackbar('Fehler beim Löschen des Objekts', 'error');
        }
      }
    }
  };

  const handleDeactivateObject = async (objektId) => {
    try {
      await apiService.post(`/api/objects/${objektId}/deactivate`);
      showSnackbar('Objekt auf inaktiv gesetzt', 'success');
      fetchObjects();
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
      showSnackbar('Fehler beim Deaktivieren', 'error');
    }
  };

  const handleActivateObject = async (objektId) => {
    try {
      await apiService.post(`/api/objects/${objektId}/activate`);
      showSnackbar('Objekt aktiviert', 'success');
      fetchObjects();
    } catch (err) {
      console.error('Fehler beim Aktivieren:', err);
      showSnackbar('Fehler beim Aktivieren', 'error');
    }
  };

  const openReassignDialog = (objekt) => {
    setReassignSourceId(objekt.objektid);
    setReassignTargetObjektId('');
    setReassignTargetWohnungsId('');
    setReassignUnits([]);
    setReassignAffected(null);
    setReassignOpen(true);
  };

  const closeReassignDialog = () => {
    setReassignOpen(false);
  };

  const handleTargetObjectChange = async (e) => {
    const value = e.target.value;
    setReassignTargetObjektId(value);
    setReassignTargetWohnungsId('');
    setReassignUnits([]);
    if (!value) return;
    try {
      setReassignLoading(true);
      const data = await apiService.get(`/api/objects/${value}/wohneinheiten`);
      setReassignUnits(data || []);
    } catch (err) {
      console.error('Fehler beim Laden der Wohneinheiten:', err);
      showSnackbar('Fehler beim Laden der Wohneinheiten des Zielobjekts', 'error');
    } finally {
      setReassignLoading(false);
    }
  };

  const handleReassignDryRun = async () => {
    if (!reassignSourceId || !reassignTargetObjektId) {
      showSnackbar('Bitte Zielobjekt auswählen', 'warning');
      return;
    }
    try {
      setReassignLoading(true);
      const payload = {
        targetObjektId: reassignTargetObjektId,
        dryRun: true,
      };
      if (reassignTargetWohnungsId) payload.targetWohnungsId = reassignTargetWohnungsId;
      const data = await apiService.post(`/api/objects/${reassignSourceId}/reassign-buchungen`, payload);
      setReassignAffected(data?.affected ?? 0);
      showSnackbar(`Betroffene Buchungen: ${data?.affected ?? 0}`, 'info');
    } catch (err) {
      console.error('Dry-Run fehlgeschlagen:', err);
      showSnackbar('Dry-Run fehlgeschlagen', 'error');
    } finally {
      setReassignLoading(false);
    }
  };

  const handleReassignExecute = async () => {
    if (!reassignSourceId || !reassignTargetObjektId) {
      showSnackbar('Bitte Zielobjekt auswählen', 'warning');
      return;
    }
    try {
      setReassignLoading(true);
      const payload = {
        targetObjektId: reassignTargetObjektId,
      };
      if (reassignTargetWohnungsId) payload.targetWohnungsId = reassignTargetWohnungsId;
      const data = await apiService.post(`/api/objects/${reassignSourceId}/reassign-buchungen`, payload);
      showSnackbar(`Buchungen umgehängt: ${data?.affected ?? 0}`, 'success');
      setReassignOpen(false);
      fetchObjects();
    } catch (err) {
      console.error('Umhängen fehlgeschlagen:', err);
      showSnackbar('Umhängen fehlgeschlagen', 'error');
    } finally {
      setReassignLoading(false);
    }
  };

  // Snackbar anzeigen
  return (
    <Container maxWidth="xl">
      <Box my={4}>
        {/* Header */}
        <StyledCard>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <BusinessIcon sx={{ fontSize: 32, color: '#1a365d', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ color: '#1a365d', fontWeight: 600 }}>
                  Objektverwaltung
                </Typography>
                <Typography variant="body1" sx={{ color: '#718096' }}>
                  Verwalten Sie Ihre Immobilienobjekte - anlegen, bearbeiten und verwalten
                </Typography>
              </Box>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box display="flex" alignItems="center">
                  <HomeIcon sx={{ color: '#d69e2e', mr: 1 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {objects.length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#718096' }}>
                      Objekte gesamt
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box display="flex" alignItems="center">
                  <EuroIcon sx={{ color: '#38a169', mr: 1 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatPrice(objects.reduce((sum, obj) => sum + (parseFloat(obj.objektkaufpreis) || 0), 0))}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#718096' }}>
                      Gesamtwert
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                  <FormControlLabel
                    control={<Switch checked={showInactive} onChange={handleToggleInactive} />}
                    label="Inaktive anzeigen"
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>

        {/* Objektliste */}
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kürzel</TableCell>
                <TableCell>Adresse</TableCell>
                <TableCell>PLZ / Ort</TableCell>
                <TableCell>Kaufdatum</TableCell>
                <TableCell>Kaufpreis</TableCell>
                <TableCell>Gebäudeanteil</TableCell>
                <TableCell>Eigentümer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {objects.map((objekt) => (
                <TableRow key={objekt.objektid} hover>
                  <TableCell>
                    <Chip 
                      label={objekt.objektkuerzel} 
                      variant="outlined" 
                      sx={{ 
                        borderColor: '#1a365d',
                        color: '#1a365d',
                        fontWeight: 600
                      }} 
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {objekt.objektstrasse} {objekt.objekthausnummer}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {objekt.objektplz} {objekt.objektort}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <DateIcon sx={{ fontSize: 16, color: '#718096', mr: 1 }} />
                      <Typography variant="body2">
                        {formatDate(objekt.objektkaufdatum)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#38a169' }}>
                      {formatPrice(objekt.objektkaufpreis)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {objekt.objektgebaeudeanteilprozent ? `${objekt.objektgebaeudeanteilprozent}%` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {objekt.eigentuemer?.length ? (
                      objekt.eigentuemer.map((e) => (
                        <Typography key={e.eigentuemer_id} variant="body2">
                          {e.eigentuemer_bezeichnung}
                          {' '}({ownerTypLabel(e)}{Number(e.anteil_prozent) !== 100 ? `, ${e.anteil_prozent} %` : ''})
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">–</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {objekt.aktiv ? (
                      <Chip label="Aktiv" size="small" color="success" variant="outlined" />
                    ) : (
                      <Chip label="Inaktiv" size="small" color="default" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <ActionButton 
                      className="edit"
                      size="small"
                      onClick={() => handleOpenDialog(objekt)}
                      title="Bearbeiten"
                    >
                      <EditIcon fontSize="small" />
                    </ActionButton>
                    <ActionButton 
                      className="edit"
                      size="small"
                      onClick={() => setDokumenteObjekt(objekt)}
                      title="Dokumente (z. B. WEG-Protokolle)"
                    >
                      <DescriptionIcon fontSize="small" />
                    </ActionButton>
                    <ActionButton 
                      className="edit"
                      size="small"
                      onClick={() => openReassignDialog(objekt)}
                      title="Buchungen umhängen"
                    >
                      <SyncAltIcon fontSize="small" />
                    </ActionButton>
                    {objekt.aktiv ? (
                      <ActionButton 
                        className="delete"
                        size="small"
                        onClick={() => handleDeactivateObject(objekt.objektid)}
                        title="Inaktiv setzen"
                      >
                        <PowerIcon fontSize="small" />
                      </ActionButton>
                    ) : (
                      <ActionButton 
                        className="edit"
                        size="small"
                        onClick={() => handleActivateObject(objekt.objektid)}
                        title="Aktivieren"
                      >
                        <PowerIcon fontSize="small" />
                      </ActionButton>
                    )}
                    <ActionButton 
                      className="delete"
                      size="small"
                      onClick={() => handleDeleteObject(objekt.objektid)}
                      title="Löschen"
                    >
                      <DeleteIcon fontSize="small" />
                    </ActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {objects.length === 0 && !loading && (
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="text.secondary">
                Keine Objekte vorhanden. Klicken Sie auf das + Symbol, um ein neues Objekt anzulegen.
              </Typography>
            </Box>
          )}
        </StyledTableContainer>

        {/* Floating Action Button */}
        <StyledFab 
          onClick={() => handleOpenDialog()}
          title="Neues Objekt anlegen"
        >
          <AddIcon />
        </StyledFab>

        {/* Dialog für Objekt hinzufügen/bearbeiten */}
        <Dialog 
          open={open} 
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              <BusinessIcon sx={{ mr: 2, color: '#1a365d' }} />
              {editMode ? 'Objekt bearbeiten' : 'Neues Objekt anlegen'}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Objektkürzel"
                  name="objektkuerzel"
                  value={formData.objektkuerzel}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Straße"
                  name="objektstrasse"
                  value={formData.objektstrasse}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Hausnummer"
                  name="objekthausnummer"
                  value={formData.objekthausnummer}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PLZ"
                  name="objektplz"
                  value={formData.objektplz}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ort"
                  name="objektort"
                  value={formData.objektort}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Kaufdatum"
                  name="objektkaufdatum"
                  type="date"
                  value={formData.objektkaufdatum}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Kaufpreis (€)"
                  name="objektkaufpreis"
                  type="number"
                  value={formData.objektkaufpreis}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Gebäudeanteil (%)"
                  name="objektgebaeudeanteilprozent"
                  type="number"
                  value={formData.objektgebaeudeanteilprozent}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Marktwert-Faktor"
                  name="marktwert_faktor"
                  type="number"
                  value={formData.marktwert_faktor}
                  onChange={handleInputChange}
                  inputProps={{ min: 0.01, max: 999.99, step: 0.1 }}
                  helperText="Faktor für Marktwert (Jahreskaltmiete × Faktor)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="eigentuemer-select-label">Eigentümer</InputLabel>
                  <Select
                    labelId="eigentuemer-select-label"
                    value={formData.eigentuemer_id}
                    label="Eigentümer"
                    onChange={(e) => {
                      if (e.target.value === '__neu__') {
                        setOwnerDialogOpen(true);
                      } else {
                        setFormData(prev => ({ ...prev, eigentuemer_id: e.target.value }));
                      }
                    }}
                  >
                    <MenuItem value="">— Kein Eigentümer —</MenuItem>
                    {eigentuemerListe.map((e) => (
                      <MenuItem key={e.eigentuemer_id} value={e.eigentuemer_id}>
                        {e.bezeichnung} ({ownerTypLabel(e)})
                      </MenuItem>
                    ))}
                    <MenuItem value="__neu__">+ Neuen Eigentümer anlegen…</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Eigentumsanteil (%)"
                  name="anteil_prozent"
                  type="number"
                  value={formData.anteil_prozent}
                  onChange={handleInputChange}
                  inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                  disabled={!formData.eigentuemer_id}
                  helperText="Anteil dieses Eigentümers am Objekt"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={handleCloseDialog}
              startIcon={<CancelIcon />}
              sx={{ color: '#718096' }}
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleSaveObject}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
              sx={{ 
                background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2d3748 0%, #1a365d 100%)',
                }
              }}
            >
              {editMode ? 'Aktualisieren' : 'Anlegen'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Buchungen umhängen */}
        <Dialog open={reassignOpen} onClose={closeReassignDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center">
              <SyncAltIcon sx={{ mr: 1 }} /> Buchungen umhängen
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="target-objekt-label">Zielobjekt</InputLabel>
                  <Select
                    labelId="target-objekt-label"
                    value={reassignTargetObjektId}
                    label="Zielobjekt"
                    onChange={handleTargetObjectChange}
                  >
                    {objects
                      .filter(o => !!o.objektid)
                      .map(o => (
                        <MenuItem key={o.objektid} value={o.objektid}>
                          {o.objektkuerzel} — {o.objektstrasse} {o.objekthausnummer}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={!reassignTargetObjektId || reassignLoading || reassignUnits.length === 0}>
                  <InputLabel id="target-wohnung-label">Ziel-Wohneinheit (optional)</InputLabel>
                  <Select
                    labelId="target-wohnung-label"
                    value={reassignTargetWohnungsId}
                    label="Ziel-Wohneinheit (optional)"
                    onChange={(e) => setReassignTargetWohnungsId(e.target.value)}
                  >
                    <MenuItem value="">— Keine — (Wohnungs-ID auf NULL setzen)</MenuItem>
                    {reassignUnits.map(we => (
                      <MenuItem key={we.wohneinheit_id} value={we.wohneinheit_id}>
                        {we.bezeichnung} ({we.wohneinheit_id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {reassignAffected !== null && (
                <Grid item xs={12}>
                  <Alert severity="info">Betroffene Buchungen: {reassignAffected}</Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeReassignDialog} startIcon={<CancelIcon />}>Abbrechen</Button>
            <Button onClick={handleReassignDryRun} disabled={reassignLoading || !reassignTargetObjektId}>Dry-Run</Button>
            <Button onClick={handleReassignExecute} variant="contained" disabled={reassignLoading || !reassignTargetObjektId}>Umhängen</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Neuen Eigentümer anlegen */}
        <Dialog open={ownerDialogOpen} onClose={() => setOwnerDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Neuen Eigentümer anlegen</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name / Firmierung"
                  value={ownerForm.bezeichnung}
                  onChange={(e) => setOwnerForm(prev => ({ ...prev, bezeichnung: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="owner-typ-label">Typ</InputLabel>
                  <Select
                    labelId="owner-typ-label"
                    value={ownerForm.eigentuemer_typ}
                    label="Typ"
                    onChange={(e) => setOwnerForm(prev => ({ ...prev, eigentuemer_typ: e.target.value }))}
                  >
                    {Object.entries(EIGENTUEMER_TYP_LABELS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {ownerForm.eigentuemer_typ === 'KAPITALGESELLSCHAFT' && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="owner-rechtsform-label">Rechtsform</InputLabel>
                    <Select
                      labelId="owner-rechtsform-label"
                      value={ownerForm.rechtsform}
                      label="Rechtsform"
                      onChange={(e) => setOwnerForm(prev => ({ ...prev, rechtsform: e.target.value }))}
                    >
                      {Object.entries(RECHTSFORM_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOwnerDialogOpen(false)} startIcon={<CancelIcon />}>Abbrechen</Button>
            <Button onClick={handleCreateOwner} variant="contained" disabled={!ownerForm.bezeichnung.trim()}>Anlegen</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar für Benachrichtigungen */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <ObjektDokumenteDialog
          objekt={dokumenteObjekt}
          open={Boolean(dokumenteObjekt)}
          onClose={() => setDokumenteObjekt(null)}
        />
      </Box>
    </Container>
  );
};

export default ObjectManagement;
